import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { formatNumber } from '../../../utils/formatNumber';

const Wrap = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 14px;
  border-bottom: 1px solid ${p => p.theme.border};
  background: ${p => p.theme.bgCard};
`;

const BreadcrumbBar = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  font-size: 12px;
  flex: 1;
`;

const ViewToggle = styled.button`
  background: ${p => p.$active ? p.theme.accent : p.theme.bgCard};
  color: ${p => p.$active ? '#fff' : p.theme.textSecondary};
  border: 1px solid ${p => p.$active ? p.theme.accent : p.theme.border};
  padding: 4px 10px;
  border-radius: ${p => p.theme.radiusSm};
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: ${p => p.theme.transition};

  &:hover {
    background: ${p => p.$active ? p.theme.accentHover : p.theme.bgHover};
  }
`;

const ListSearch = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid ${p => p.theme.borderInput};
  border-radius: ${p => p.theme.radiusSm};
  background: ${p => p.theme.bgInput};
  color: ${p => p.theme.text};
  font-size: 12px;
  box-sizing: border-box;
  margin-bottom: 10px;

  &:focus {
    outline: none;
    border-color: ${p => p.theme.borderInputFocus};
  }
`;

const ListGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 8px;

  @media screen and (max-width: 540px) {
    grid-template-columns: 1fr;
  }
`;

const ListItemCard = styled.button`
  text-align: left;
  background: ${p => p.$current ? p.theme.bgSelected : p.theme.bgCard};
  border: 1px solid ${p => {
    if (p.$current) return p.theme.borderSelected;
    if (p.$isOrphan) return '#fde68a';
    return p.theme.border;
  }};
  border-radius: ${p => p.theme.radiusSm};
  padding: 10px 12px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 4px;
  transition: ${p => p.theme.transition};

  &:hover {
    border-color: ${p => p.theme.accent};
  }
`;

const ListItemTitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
`;

const ListItemTitle = styled.span`
  font-size: 13px;
  font-weight: 700;
  color: ${p => p.theme.text};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
`;

const ListItemMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 11px;
  color: ${p => p.theme.textMuted};
`;

const ListItemDesc = styled.div`
  font-size: 11px;
  color: ${p => p.theme.textMuted};
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const BackButton = styled.button`
  background: ${p => p.theme.bgCard};
  border: 1px solid ${p => p.theme.border};
  color: ${p => p.theme.textSecondary};
  padding: 4px 10px;
  border-radius: ${p => p.theme.radiusSm};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  margin-right: 8px;
  transition: ${p => p.theme.transition};

  &:hover:not(:disabled) {
    background: ${p => p.theme.bgHover};
    color: ${p => p.theme.text};
  }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const BreadcrumbCrumb = styled.span`
  cursor: ${p => p.$active ? 'default' : 'pointer'};
  color: ${p => p.$active ? p.theme.text : p.theme.accent};
  font-weight: ${p => p.$active ? 700 : 500};
  padding: 2px 6px;
  border-radius: 4px;
  transition: ${p => p.theme.transition};

  &:hover {
    background: ${p => p.$active ? 'transparent' : p.theme.accentLight};
  }
`;

const Sep = styled.span`
  color: ${p => p.theme.textMuted};
  font-size: 11px;
`;

const Body = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 14px;
`;

const CurrentCard = styled.div`
  background: ${p => p.theme.bgCard};
  border: 2px solid ${p => p.$isStart ? p.theme.success : p.theme.border};
  border-radius: ${p => p.theme.radius};
  padding: 14px 16px;
  margin-bottom: 14px;
`;

const CurrentTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const CurrentTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: ${p => p.theme.text};
`;

const TypeBadge = styled.span`
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 4px;
  border: 1px solid ${p => p.$multi ? p.theme.success : p.theme.accent};
  color: ${p => p.$multi ? p.theme.success : p.theme.accent};
  background: ${p => p.theme.bgCard};
`;

const StatusPill = styled.span`
  font-size: 10px;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 4px;
  ${p => p.$variant === 'start' && `background: #d1fae5; color: #047857;`}
  ${p => p.$variant === 'orphan' && `background: #fef3c7; color: #b45309;`}
`;

const CurrentDesc = styled.p`
  margin: 6px 0 0;
  font-size: 12px;
  color: ${p => p.theme.textMuted};
  line-height: 1.5;
`;

const SectionTitle = styled.h4`
  margin: 14px 0 8px;
  font-size: 13px;
  font-weight: 700;
  color: ${p => p.theme.text};
  display: flex;
  align-items: center;
  gap: 8px;

  small {
    color: ${p => p.theme.textMuted};
    font-size: 11px;
    font-weight: 500;
  }
`;

const ItemGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 10px;

  @media screen and (max-width: 540px) {
    grid-template-columns: 1fr;
  }
`;

const ItemCard = styled.button`
  text-align: left;
  background: ${p => p.theme.bgCard};
  border: 1px solid ${p => p.$hasNext ? p.theme.accent : p.theme.border};
  border-radius: ${p => p.theme.radiusSm};
  padding: 12px;
  cursor: ${p => p.$hasNext ? 'pointer' : 'default'};
  display: flex;
  flex-direction: column;
  gap: 6px;
  transition: ${p => p.theme.transition};

  &:hover {
    background: ${p => p.$hasNext ? p.theme.accentLight : p.theme.bgInput};
    border-color: ${p => p.theme.accent};
  }
`;

const ItemName = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: ${p => p.theme.text};
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 8px;
`;

const ItemImage = styled.div`
  width: 100%;
  aspect-ratio: 16 / 10;
  border-radius: 6px;
  background: ${p => p.theme.bgInput};
  border: 1px solid ${p => p.theme.border};
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 4px;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  span.placeholder {
    font-size: 11px;
    color: ${p => p.theme.textMuted};
    font-weight: 600;
  }
`;

const ItemPrice = styled.span`
  font-size: 12px;
  font-weight: 700;
  color: ${p => p.theme.accent};
  white-space: nowrap;
`;

const ItemDesc = styled.div`
  font-size: 11px;
  color: ${p => p.theme.textMuted};
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  min-height: 30px;
`;

const NextLink = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: ${p => p.theme.accent};
  background: ${p => p.theme.accentLight};
  padding: 4px 8px;
  border-radius: 4px;
  font-weight: 600;
  margin-top: 4px;
`;

const EndTag = styled.div`
  font-size: 11px;
  color: ${p => p.theme.textMuted};
  padding: 4px 8px;
  background: ${p => p.theme.bgInput};
  border-radius: 4px;
  margin-top: 4px;
  font-weight: 500;
`;

const LinkButton = styled.button`
  font-size: 11px;
  color: ${p => p.theme.accent};
  background: ${p => p.theme.bgCard};
  border: 1px dashed ${p => p.theme.accent};
  padding: 4px 8px;
  border-radius: 4px;
  margin-top: 4px;
  font-weight: 600;
  cursor: pointer;
  transition: ${p => p.theme.transition};

  &:hover {
    background: ${p => p.theme.accentLight};
    border-style: solid;
  }
`;

const Empty = styled.div`
  padding: 30px;
  text-align: center;
  color: ${p => p.theme.textMuted};
  font-size: 13px;
  background: ${p => p.theme.bgCard};
  border: 1px dashed ${p => p.theme.border};
  border-radius: ${p => p.theme.radius};
`;

const OrphanCard = styled.div`
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: ${p => p.theme.radiusSm};
  padding: 10px 12px;
  margin-bottom: 10px;
  font-size: 12px;
  color: #92400e;
  display: flex;
  flex-direction: column;
  gap: 6px;

  strong { color: #78350f; }
`;

const OrphanList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 4px;
`;

const OrphanChip = styled.button`
  font-size: 11px;
  background: #fef3c7;
  border: 1px solid #fde68a;
  color: #b45309;
  padding: 3px 8px;
  border-radius: 12px;
  cursor: pointer;
  font-weight: 600;

  &:hover {
    background: #fde68a;
  }
`;

export default function DrillDownView({
  categories,
  itemsByCategory,
  groupKey,
  selectedCategory,
  onSelectCategory,
  onLinkNext,
  wizardRoot,
  wizardReachable,
  priceUnit = '만원',
}) {
  // path: 카테고리 이름 배열, 시작은 wizardRoot
  const [path, setPath] = useState([]);
  // viewMode: 'drill' (기본 drill-down) | 'list' (전체 카테고리 평면 그리드)
  const [viewMode, setViewMode] = useState('drill');
  const [listSearch, setListSearch] = useState('');

  // 초기/그룹 변경 시 wizardRoot로 초기화
  useEffect(() => {
    if (wizardRoot && categories.find(c => c.main_category === wizardRoot)) {
      setPath([wizardRoot]);
    } else if (categories.length > 0) {
      setPath([categories[0].main_category]);
    } else {
      setPath([]);
    }
  }, [groupKey, wizardRoot, categories.length]);

  // 외부에서 selectedCategory 가 바뀌어도 path 마지막과 다르면 path 갱신
  useEffect(() => {
    if (!selectedCategory) return;
    const currentName = path[path.length - 1];
    if (currentName === selectedCategory.main_category) return;
    // path 안에 있으면 그 위치까지 잘라냄, 없으면 새로 시작
    const idx = path.indexOf(selectedCategory.main_category);
    if (idx >= 0) {
      setPath(path.slice(0, idx + 1));
    } else {
      setPath([selectedCategory.main_category]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory]);

  const currentName = path[path.length - 1];
  const currentCat = useMemo(
    () => categories.find(c => c.main_category === currentName),
    [categories, currentName]
  );
  const currentItems = useMemo(
    () => itemsByCategory[`${groupKey}::${currentName}`] || [],
    [itemsByCategory, groupKey, currentName]
  );

  // current 가 바뀌면 외부에 알려서 우측 상세 패널이 sync
  useEffect(() => {
    if (currentCat && (!selectedCategory || selectedCategory.main_category !== currentCat.main_category)) {
      onSelectCategory(currentCat);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCat]);

  const goBack = () => {
    if (path.length > 1) setPath(path.slice(0, -1));
  };

  const goToCrumb = (idx) => {
    setPath(path.slice(0, idx + 1));
  };

  const drillInto = (nextCategoryName) => {
    if (!nextCategoryName) return;
    if (!categories.find(c => c.main_category === nextCategoryName)) return;
    setPath([...path, nextCategoryName]);
  };

  // 현재 path 에 없는 root + 고아 카테고리 (다른 흐름에서 시작)
  const otherRoots = useMemo(() => {
    return categories
      .filter(c => !wizardReachable.has(c.main_category))
      .filter(c => c.main_category !== currentName);
  }, [categories, wizardReachable, currentName]);

  const filteredListCats = useMemo(() => {
    if (!listSearch) return categories;
    const q = listSearch.toLowerCase();
    return categories.filter(c =>
      (c.main_category || '').toLowerCase().includes(q) ||
      (c.info || '').toLowerCase().includes(q)
    );
  }, [categories, listSearch]);

  if (!currentCat) {
    return (
      <Wrap>
        <Body>
          <Empty>표시할 카테고리가 없습니다.</Empty>
        </Body>
      </Wrap>
    );
  }

  const isStart = currentCat.main_category === wizardRoot;
  const isReachable = wizardReachable.has(currentCat.main_category);
  const isOrphan = !isReachable;
  const isMulti = currentCat.category_type === 'M';

  return (
    <Wrap>
      <TopBar>
        <BreadcrumbBar>
          {viewMode === 'drill' ? (
            <>
              <BackButton onClick={goBack} disabled={path.length <= 1}>← 뒤로</BackButton>
              {path.map((p, idx) => (
                <React.Fragment key={`${p}-${idx}`}>
                  {idx > 0 && <Sep>›</Sep>}
                  <BreadcrumbCrumb
                    $active={idx === path.length - 1}
                    onClick={() => idx !== path.length - 1 && goToCrumb(idx)}
                  >
                    {p}
                  </BreadcrumbCrumb>
                </React.Fragment>
              ))}
            </>
          ) : (
            <span style={{ fontSize: 12, color: '#64748b' }}>
              전체 카테고리 <strong style={{ color: '#1e293b' }}>{categories.length}개</strong>
            </span>
          )}
        </BreadcrumbBar>
        <ViewToggle
          $active={viewMode === 'drill'}
          onClick={() => setViewMode('drill')}
        >단계 진행</ViewToggle>
        <ViewToggle
          $active={viewMode === 'list'}
          onClick={() => setViewMode('list')}
        >전체 목록</ViewToggle>
      </TopBar>

      <Body>
        {viewMode === 'list' && (
          <>
            <ListSearch
              placeholder="카테고리 이름 / 설명 검색..."
              value={listSearch}
              onChange={(e) => setListSearch(e.target.value)}
            />
            {filteredListCats.length === 0 ? (
              <Empty>검색 결과가 없습니다.</Empty>
            ) : (
              <ListGrid>
                {filteredListCats.map(cat => {
                  const items = itemsByCategory[`${groupKey}::${cat.main_category}`] || [];
                  const isCurrent = cat.main_category === currentName;
                  const isStartCat = cat.main_category === wizardRoot;
                  const isOrphanCat = !wizardReachable.has(cat.main_category);
                  return (
                    <ListItemCard
                      key={cat.main_category}
                      $current={isCurrent}
                      $isOrphan={isOrphanCat && !isStartCat}
                      onClick={() => {
                        setPath([cat.main_category]);
                        setViewMode('drill');
                      }}
                    >
                      <ListItemTitleRow>
                        <ListItemTitle>{cat.main_category}</ListItemTitle>
                        {isStartCat && <StatusPill $variant="start">시작점</StatusPill>}
                        {isOrphanCat && !isStartCat && <StatusPill $variant="orphan">고아</StatusPill>}
                      </ListItemTitleRow>
                      {cat.info && <ListItemDesc>{cat.info}</ListItemDesc>}
                      <ListItemMeta>
                        <span>{cat.category_type === 'M' ? '다중' : '단일'} 선택</span>
                        <span>{items.length}개 항목</span>
                      </ListItemMeta>
                    </ListItemCard>
                  );
                })}
              </ListGrid>
            )}
          </>
        )}

        {viewMode === 'drill' && (<>
        <CurrentCard $isStart={isStart}>
          <CurrentTitleRow>
            <CurrentTitle>{currentCat.main_category}</CurrentTitle>
            <TypeBadge $multi={isMulti}>{isMulti ? '다중 선택' : '단일 선택'}</TypeBadge>
            {isStart && <StatusPill $variant="start">Wizard 시작점</StatusPill>}
            {isOrphan && !isStart && <StatusPill $variant="orphan">고아 (진입 불가)</StatusPill>}
          </CurrentTitleRow>
          {currentCat.info && <CurrentDesc>{currentCat.info}</CurrentDesc>}
        </CurrentCard>

        <SectionTitle>
          이 카테고리의 항목
          <small>· 클릭하면 다음 카테고리로 이동합니다 ({currentItems.length}개)</small>
        </SectionTitle>

        {currentItems.length === 0 ? (
          <Empty>등록된 항목이 없습니다.</Empty>
        ) : (
          <ItemGrid>
            {currentItems.map((item, idx) => {
              const hasNext = !!item.next_category &&
                categories.some(c => c.main_category === item.next_category);
              return (
                <ItemCard
                  key={`${item.item_name}-${idx}`}
                  $hasNext={hasNext}
                  onClick={() => hasNext && drillInto(item.next_category)}
                >
                  {item._has_image && (
                    <ItemImage>
                      {item.image_url ? (
                        <img src={item.image_url} alt={item.item_name} />
                      ) : (
                        <span className="placeholder">사진 없음</span>
                      )}
                    </ItemImage>
                  )}
                  <ItemName>
                    <span>{item.item_name}</span>
                    <ItemPrice>{formatNumber(item.item_price)}{priceUnit}</ItemPrice>
                  </ItemName>
                  <ItemDesc>{item.item_description || '설명 없음'}</ItemDesc>
                  {hasNext ? (
                    <NextLink>→ 다음: {item.next_category}</NextLink>
                  ) : onLinkNext ? (
                    <LinkButton
                      onClick={(e) => {
                        e.stopPropagation();
                        onLinkNext(item);
                      }}
                      title="이 항목에 다음 카테고리 연결"
                    >
                      + 다음 카테고리 연결
                    </LinkButton>
                  ) : (
                    <EndTag>· 여기서 종료</EndTag>
                  )}
                </ItemCard>
              );
            })}
          </ItemGrid>
        )}

        {/* 현재 wizard 흐름 외 고아 카테고리들 빠른 이동 */}
        {path.length === 1 && otherRoots.length > 0 && (
          <>
            <SectionTitle>
              Wizard 흐름 밖 카테고리
              <small>· {otherRoots.length}개 (어느 항목의 next_category로도 가리키지 않음)</small>
            </SectionTitle>
            <OrphanCard>
              <strong>이 카테고리들은 견적서 메인 wizard에서 진입 경로가 없어 표시되지 않습니다.</strong>
              <span>다른 항목의 next_category로 연결하거나, 사용 안 하면 삭제하세요.</span>
              <OrphanList>
                {otherRoots.map(c => (
                  <OrphanChip key={c.main_category} onClick={() => setPath([c.main_category])}>
                    {c.main_category}
                  </OrphanChip>
                ))}
              </OrphanList>
            </OrphanCard>
          </>
        )}
        </>)}
      </Body>
    </Wrap>
  );
}
